import axios, { AxiosInstance } from 'axios';

// Use direct API URL - make sure backend has CORS enabled
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5116';

// Token storage key (should match AuthStore)
const AUTH_TOKEN_KEY = 'smartai_auth_token';

export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  // Add timeout and other settings - 5 minutes for long-running operations
  timeout: 300000,
});

// Request interceptor - adds Bearer token automatically
apiClient.interceptors.request.use(
  (config) => {
    // Get token from localStorage (matches AuthStore implementation)
    const token = localStorage.getItem(AUTH_TOKEN_KEY);

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor - handles auth errors and token expiry
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Handle 401 Unauthorized responses
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Don't logout during sync-session or initial auth flow
      const isAuthFlow = window.location.pathname.includes('/login') ||
                         window.location.pathname.includes('/register') ||
                         window.location.pathname.includes('/sync-session');

      if (isAuthFlow) {
        console.log('401 during auth flow, not clearing auth state');
        originalRequest._retry = true;
        return Promise.reject(error);
      }

      console.log('401 Unauthorized response, clearing auth and redirecting to login');

      // Clear all auth data
      localStorage.removeItem(AUTH_TOKEN_KEY);
      localStorage.removeItem('refreshToken');

      // Clear auth store state (this will be handled by the store's logout method)
      // Note: We import and use the store dynamically to avoid circular dependencies
      try {
        const { useAuthStore } = await import('../stores/AuthStore');
        useAuthStore.getState().logout();
      } catch (storeError) {
        console.error('Failed to clear auth store:', storeError);
      }

      // Redirect to login
      window.location.href = '/login';

      originalRequest._retry = true;
    }

    // Handle 429 Rate Limiting
    if (error.response?.status === 429) {
      console.warn('Rate limit exceeded, request failed');
    }

    // Handle network errors
    if (!error.response) {
      console.error('Network error or timeout:', error.message);
    }

    return Promise.reject(error);
  }
);